import { PageSubtitleShort } from '@zoopla-eng/zds-components/headings';
import { Link } from '@zoopla-eng/zds-components/link';
import { Divider } from '@zoopla-eng/zds-components/divider';
import { Box } from '@zoopla-eng/zds-components/box';
import { fontGroups } from '@zoopla-eng/zds-components/themes/font-groups';

import {
    remortgageFigureWrapper,
    remortgageInnerWrapper,
    barChartImg,
    figureWrapper,
    boldFont,
    barChartCite,
} from './RemortgageFigure.css';

import RemortgageBarChartSmall from '../../../assets/remortgage-barchart-sm.svg';
import RemortgageLineChartSmall from '../../../assets/remortgage-linechart-sm.svg';

type Copy = {
    title: string;
    bodyHeader: string | JSX.Element;
    bodyFooter?: string;
    chartTitle: string;
    chartBody?: string;
    alt: string;
    sourceUrl: string;
};

type RemortgageFigureProps = {
    copy: Copy;
    type: 'line' | 'bar';
    // controls whether the columns are reversed or not.
    reverseRow?: boolean;
    // controls whether there is a contrast background.
    contrastBackground?: boolean;
};

const RemortgageFigure = ({
    copy,
    type = 'bar',
    reverseRow = false,
    contrastBackground = false,
}: RemortgageFigureProps) => {
    const defaultChartUrl =
        type === 'bar' ? RemortgageBarChartSmall : RemortgageLineChartSmall;
    return (
        <div className={remortgageFigureWrapper({ contrastBackground })}>
            <div className={remortgageInnerWrapper({ reverseRow })}>
                <Box
                    display="flex"
                    flexDirection="column"
                    rowGap="S500"
                    bpL100={{
                        pr: 'M300',
                    }}
                    flex={1}
                >
                    <PageSubtitleShort>{copy.title}</PageSubtitleShort>
                    <p>{copy.bodyHeader}</p>
                    {copy.bodyFooter && <p>{copy.bodyFooter}</p>}

                    {type === 'line' && (
                        <Box
                            pt="S400"
                            display="none"
                            flexDirection="column"
                            bpL100={{ display: 'flex' }}
                        >
                            <Divider direction="horizontal" />
                            <div>
                                <Box
                                    pt="M200"
                                    display="flex"
                                    flexDirection="column"
                                    rowGap="S400"
                                >
                                    <p className={boldFont}>
                                        Remember, remortgaging can take a few
                                        weeks.
                                    </p>
                                    <p>
                                        The good news is you can sort out your
                                        remortgage and secure a great new deal
                                        up to 6 months before your current deal
                                        ends.
                                    </p>
                                </Box>
                            </div>
                        </Box>
                    )}
                </Box>

                <figure className={figureWrapper}>
                    <figcaption className={`${boldFont} ${fontGroups.fork}`}>
                        {copy.chartTitle}
                    </figcaption>
                    <p className={fontGroups.fork}>{copy.chartBody}</p>
                    <img
                        className={barChartImg({ type })}
                        src={defaultChartUrl}
                        alt={copy.alt}
                    />
                    <cite className={barChartCite}>
                        Source{' '}
                        <Link href={copy.sourceUrl} target="_blank">
                            Bank of England
                        </Link>
                    </cite>
                </figure>
            </div>

            {type === 'line' && (
                <Box
                    pt="S400"
                    display="flex"
                    flexDirection="column"
                    bpL100={{ display: 'none' }}
                >
                    <Divider direction="horizontal" />
                    <div>
                        <Box
                            pt="M200"
                            display="flex"
                            flexDirection="column"
                            rowGap="S400"
                        >
                            <p className={boldFont}>
                                Remember, remortgaging can take a few weeks.
                            </p>
                            <p>
                                The good news is you can sort out your
                                remortgage and secure a great new deal up to 6
                                months before your current deal ends.
                            </p>
                        </Box>
                    </div>
                </Box>
            )}
        </div>
    );
};

export default RemortgageFigure;
